import { Map } from 'immutable';
import { ReduxAction } from 'types/redux.types';
import actions from './actions';
import { JobIncomplete } from '__generated__/graphql';

const LOCAL_STORAGE_KEY = 'hiddenContinuableJobs';

interface IncompleteJobsState {
  incompleteJobs: JobIncomplete[];
  isLoading: boolean;
  error: string | null;
}

const initialState: Map<keyof IncompleteJobsState, IncompleteJobsState[keyof IncompleteJobsState]> =
  Map<keyof IncompleteJobsState, IncompleteJobsState[keyof IncompleteJobsState]>({
    incompleteJobs: [],
    isLoading: false,
    error: null,
  });

/* Helper functions */
/**
 * 
 * @param jobs Array of JobIncomplete objects to filter
 * @returns Filtered array of JobIncomplete objects excluding hidden jobs
 */
const filterHiddenJobs = (jobs: JobIncomplete[]): JobIncomplete[] => {
  try {
    const hiddenJobs: string[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '[]');
    return jobs.filter(job => !hiddenJobs.includes(job.uid));
  } catch (error) {
    console.error('Error reading hidden jobs from localStorage:', error);
    return jobs;
  }
};

export default function reducer(
  state = initialState,
  action: ReduxAction
): Map<keyof IncompleteJobsState, IncompleteJobsState[keyof IncompleteJobsState]> {
  switch (action.type) {
    case actions.FETCH_INCOMPLETE_JOB_START:
      return state
        .set('isLoading', true)
        .set('error', null);

        
    case actions.FETCH_INCOMPLETE_JOB_DONE: {
      const jobs: JobIncomplete[] = action.payload || [];
      const visibleJobs = filterHiddenJobs(jobs);
      return state
        .set('incompleteJobs', visibleJobs)
        .set('isLoading', false)
        .set('error', null);
    }


    case actions.FETCH_INCOMPLETE_JOB_ERROR:
      return state
        .set('isLoading', false)
        .set('error', action.payload);


    /**
     * Removes a job from the incompleteJobs array in the state
     * based on the provided UID.
     */
    case actions.REMOVE_JOB_FROM_STATE: {
      const uId: string = action.payload;
      const updatedJobs = (state.get('incompleteJobs') as JobIncomplete[])
        .filter(job => job.uid !== uId);
      return state.set('incompleteJobs', updatedJobs);
    }

    /**
     * Hides a job by adding it to localStorage AND removing from state.
     */
    case actions.HIDE_JOB: {
      const uId: string = action.payload;

      // Add to localStorage
      try {
        const hiddenJobs: string[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '[]');
        if (!hiddenJobs.includes(uId)) {
          hiddenJobs.push(uId);
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(hiddenJobs));
        }
      } catch (error) {
        console.error('Error updating hidden jobs in localStorage:', error);
      }

      // Remove from state
      const updatedJobs = (state.get('incompleteJobs') as JobIncomplete[])
        .filter(job => job.uid !== uId);
      return state.set('incompleteJobs', updatedJobs);
    }

    default:
      return state;
  }
}
